import type { TableFiltersState } from '@ohpen/enso-ui';

/** The grid's column filters, taken off the state snapshot: enso does not export the type itself. */
export type ListColumnFilters = TableFiltersState['byColumn'];

type ListFilterValue = ListColumnFilters[string];

/**
 * Where a list lives and how its state is named in the query string. `tableId` is the enso Table's
 * `id`, which also fixes the global search param: enso reads `search-<tableId>` when the table
 * initialises, and PMP already links between lists with it.
 */
export interface ListUrlSpec {
  route: string;
  tableId: string;
  /** Params outside the grid that this list honours, on top of the search and `f.<column>` filters. */
  params: readonly string[];
  /**
   * The tab this list lives on, for a route that renders more than one. A list with no tab owns its
   * route on its own. Only the list whose tab is open reads the URL or reports itself to the assistant,
   * so two lists sharing a URL never answer for each other.
   */
  tab?: string;
}

export const LIST_FILTER_PREFIX = 'f.';

/**
 * The prefix that marks one list's column filters: `f.<tableId>.`. The table id scopes the key so two
 * lists rendered on one route (the tabbed Fees and Procuration Fees pages) cannot read or clear each
 * other's filters. A key without it belongs to no list and is left alone.
 */
export const listFilterPrefix = (spec: ListUrlSpec): string => `${LIST_FILTER_PREFIX}${spec.tableId}.`;

/** The column a key names, or undefined when the key belongs to another list or names no column. */
const filterColumn = (key: string, spec: ListUrlSpec): string | undefined => {
  const prefix = listFilterPrefix(spec);
  if (!key.startsWith(prefix)) return undefined;

  const column = key.slice(prefix.length);

  // Column keys are plain identifiers, so a remaining dot means a hand-edited key, not a column.
  return column === '' || column.includes('.') ? undefined : column;
};

const isFilterKey = (key: string, spec: ListUrlSpec): boolean => filterColumn(key, spec) !== undefined;

export const LIST_TAB_PARAM = 'tab';

/**
 * Which tab of a tabbed page is open. A page decides its own keys, so this is not per entity: one
 * tabbed route has one open tab, whichever of its lists that is.
 */
export const readTabParam = (params: URLSearchParams, keys: readonly string[]): string | undefined => {
  const wanted = params.get(LIST_TAB_PARAM);

  // A key that is not on the page (renamed, or a hand-edited URL) falls back rather than opening
  // nothing, and is left in the query string: rewriting it would navigate on mount.
  return wanted !== null && keys.includes(wanted) ? wanted : keys[0];
};

export const writeTabParam = (params: URLSearchParams, key: string): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.set(LIST_TAB_PARAM, key);

  return next;
};

/**
 * Whether this list is the one the user is looking at. A tabbed page writes its open tab into the URL,
 * so the question is answerable from the query string alone: no list has to be told about its siblings.
 * A list on no tab is always on screen, which is every single-list route.
 */
export const isListOnScreen = (params: URLSearchParams, spec: ListUrlSpec): boolean =>
  spec.tab === undefined || spec.tab === params.get(LIST_TAB_PARAM);

export const PRODUCTS_YEARS_PARAM = 'years';
export const PRODUCTS_ACTIVE_TODAY_PARAM = 'activeToday';

// Keyed by the entity type the list reports to the assistant, so both sides name a list the same way.
export const LIST_URL_SPECS: Readonly<Record<string, ListUrlSpec>> = {
  PRODUCTS: { route: '/products', tableId: 'PRODUCTS', params: [PRODUCTS_YEARS_PARAM, PRODUCTS_ACTIVE_TODAY_PARAM] },
  CHANGE_REQUESTS: { route: '/change-requests', tableId: 'change-requests', params: [] },
  LENDER_FIRMS: { route: '/firms', tableId: 'LENDER_FIRMS', params: [] },
  BROKERS: { route: '/brokers', tableId: 'BROKERS', params: [] },
  BASE_RATES: { route: '/rates', tableId: 'BASE_RATES', params: [] },
  EXCLUSIVITY_SETS: { route: '/exclusives', tableId: 'EXCLUSIVITY_SETS', params: [] },
  ERC_PROFILES: { route: '/erc', tableId: 'ERC_PROFILES', params: [] },
  EXCHANGE_RATES: { route: '/exchange-rates', tableId: 'EXCHANGE_RATES', params: [] },
  FEES: { route: '/fees', tableId: 'FEES', params: [], tab: 'fees' },
  FEE_PROFILES: { route: '/fees', tableId: 'FEE_PROFILES', params: [], tab: 'profiles' },
  PROCURATION_FEES: { route: '/procuration-fees', tableId: 'PROCURATION_FEES', params: [], tab: 'fees' },
  PROCURATION_FEE_RULES: { route: '/procuration-fees', tableId: 'PROCURATION_FEE_RULES', params: [], tab: 'rules' },
  REGIONS: { route: '/lender', tableId: 'regions', params: [], tab: 'regions' },
  BDMS: { route: '/lender', tableId: 'bdms', params: [], tab: 'bdms' },
  DEFAULT_PROCURATION_FEE_ASSIGNMENTS: { route: '/lender', tableId: 'LENDERSProcurationFees', params: [], tab: 'procuration-fees' },
};

/**
 * How one list's out-of-grid filters travel in the query string. `toParams` maps them to param values,
 * where `undefined` leaves the param out, and `fromParams` reads them back over the defaults. The names
 * themselves are the registry's, not the codec's.
 */
export interface ExternalFiltersUrlCodec<T> {
  toParams: (filters: T) => Record<string, string | undefined>;
  fromParams: (params: URLSearchParams, defaults: T) => T;
}

export const hasAnyParam = (params: URLSearchParams, names: readonly string[]): boolean => names.some(name => params.has(name));

/** Only the named params are written; anything else in the query string is left alone. */
export const writeExternalFilterParams = (params: URLSearchParams, patch: Record<string, string | undefined>): URLSearchParams => {
  const next = new URLSearchParams(params);

  Object.entries(patch).forEach(([name, value]) => {
    if (value === undefined) next.delete(name);
    else next.set(name, value);
  });

  return next;
};

export const listSearchParam = (spec: ListUrlSpec): string => `search-${spec.tableId}`;

export interface ListUrlState {
  search: string;
  columnFilters: ListColumnFilters;
}

// A filter value can contain a comma, so each value is encoded before they are joined.
const encodeFilterValues = (values: readonly unknown[]): string => values.map(value => encodeURIComponent(String(value))).join(',');

const decodeFilterValues = (raw: string): string[] =>
  raw
    .split(',')
    .filter(part => part !== '')
    .map(part => {
      try {
        return decodeURIComponent(part);
      } catch {
        return part;
      }
    });

const isEmptyFilter = (value: ListFilterValue | undefined): boolean =>
  value === null || value === undefined || (Array.isArray(value) && value.length === 0);

const sameFilter = (left: ListFilterValue | undefined, right: ListFilterValue | undefined): boolean => {
  if (isEmptyFilter(left) && isEmptyFilter(right)) return true;
  if (isEmptyFilter(left) || isEmptyFilter(right)) return false;
  return encodeFilterValues(left as unknown[]) === encodeFilterValues(right as unknown[]);
};

/**
 * Whether the query string carries this list's search or column filters at all. An absent param and
 * an empty one are different things here: with nothing in the URL the remembered view stands, so the
 * caller must not read an empty state and apply it over what was restored from storage.
 */
export const hasListUrlState = (params: URLSearchParams, spec: ListUrlSpec): boolean =>
  params.has(listSearchParam(spec)) || [...params.keys()].some(key => isFilterKey(key, spec));

export const readListUrlState = (params: URLSearchParams, spec: ListUrlSpec): ListUrlState => {
  const columnFilters: ListColumnFilters = {};

  params.forEach((value, key) => {
    const column = filterColumn(key, spec);
    if (column === undefined) return;

    const values = decodeFilterValues(value);
    if (values.length > 0) columnFilters[column] = values;
  });

  return { search: params.get(listSearchParam(spec))?.trim() ?? '', columnFilters };
};

/** Only the keys this list owns are written; anything else in the query string is left alone. */
export const writeListUrlState = (params: URLSearchParams, spec: ListUrlSpec, patch: Partial<ListUrlState>): URLSearchParams => {
  const next = new URLSearchParams(params);

  if (patch.search !== undefined) {
    const search = patch.search.trim();
    if (search === '') next.delete(listSearchParam(spec));
    else next.set(listSearchParam(spec), search);
  }

  if (patch.columnFilters !== undefined) {
    [...next.keys()].filter(key => isFilterKey(key, spec)).forEach(key => next.delete(key));

    Object.keys(patch.columnFilters)
      .sort((left, right) => left.localeCompare(right))
      .forEach(column => {
        const value = patch.columnFilters?.[column];
        if (!isEmptyFilter(value)) next.set(`${listFilterPrefix(spec)}${column}`, encodeFilterValues(value as unknown[]));
      });
  }

  return next;
};

/**
 * What to hand `setFilters` to make the grid match `target`. Columns the grid does not have are
 * ignored, and a column the grid still filters by but `target` does not mention is cleared explicitly,
 * because `setFilters` merges rather than replaces.
 */
export const buildFilterPatch = (target: ListColumnFilters, current: ListColumnFilters, columns: readonly string[]): ListColumnFilters => {
  const patch: ListColumnFilters = {};

  Object.entries(target).forEach(([column, value]) => {
    if (!columns.includes(column)) return;
    if (!sameFilter(current[column], value)) patch[column] = value;
  });

  Object.entries(current).forEach(([column, value]) => {
    if (isEmptyFilter(value) || column in target) return;
    patch[column] = null;
  });

  return patch;
};
